import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import PushPinIcon from '@mui/icons-material/PushPin';
import { Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

type RoomPolicyListItem = string | { text: string; pinned: boolean };

interface RoomPolicyListEditorProps {
  title: string;
  items: Array<RoomPolicyListItem>;
  onChange: (items: Array<RoomPolicyListItem>) => void;
  firstItemLocked?: boolean;
  firstItemLabel?: string;
  enablePinning?: boolean;
}

export default function RoomPolicyListEditor({
  title,
  items,
  onChange,
  firstItemLocked = false,
  firstItemLabel,
  enablePinning = false,
}: RoomPolicyListEditorProps) {
  const getItem = (item: RoomPolicyListItem) => (typeof item === 'string' ? { text: item, pinned: false } : item);

  const updateItem = (index: number, item: { text: string; pinned: boolean }) => {
    const updatedItems = [...items];
    updatedItems[index] = item.pinned ? item : item.text;
    onChange(updatedItems);
  };

  const handleItemChange = (index: number, value: string) => {
    updateItem(index, { ...getItem(items[index]), text: value });
  };

  const handleAddItem = () => {
    onChange([...items, '']);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    onChange(updatedItems);
  };

  const handleTogglePinned = (index: number) => {
    const item = getItem(items[index]);
    updateItem(index, { ...item, pinned: !item.pinned });
  };

  const isLocked = (index: number) => firstItemLocked && index === 0;

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {enablePinning && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Pinned notes are displayed under “Maximum room capacity”.
        </Typography>
      )}
      <Stack spacing={1}>
        {items.map((item, index) => (
          <Stack
            key={index}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={getItem(item).pinned ? { backgroundColor: 'action.hover', borderRadius: 1, p: 0.5 } : undefined}
          >
            <TextField
              fullWidth
              size="small"
              value={getItem(item).text}
              onChange={(e) => handleItemChange(index, e.target.value)}
              disabled={isLocked(index)}
            />
            {enablePinning && (
              <Tooltip title={getItem(item).pinned ? 'Unpin note' : 'Pin note'}>
                <IconButton
                  size="small"
                  color={getItem(item).pinned ? 'primary' : 'default'}
                  onClick={() => handleTogglePinned(index)}
                  disabled={isLocked(index)}
                  aria-label={getItem(item).pinned ? 'Unpin note' : 'Pin note'}
                >
                  <PushPinIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isLocked(index) ? (
              <>
                {firstItemLabel && (
                  <Chip
                    icon={<LockIcon fontSize="small" />}
                    label={firstItemLabel}
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                )}
                <Tooltip title="This is the room default and cannot be removed">
                  <span>
                    <IconButton size="small" disabled>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              <IconButton size="small" onClick={() => handleRemoveItem(index)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
      </Stack>
      <Button size="small" startIcon={<AddIcon />} onClick={handleAddItem} sx={{ mt: 1 }}>
        Add item
      </Button>
    </Box>
  );
}
